import { useId, useState } from 'react';
import { Drawer } from '@material-ui/core';
import { IconChevronDown, IconChevronRight, IconHelp } from '@tabler/icons';
import clsx from 'clsx';

import { useResponsive } from '#root/hooks';
import DrawerAppbar from '../DrawerAppbar';
import styles from './faqPanel.module.scss';

export interface FAQItem {
  id: string;
  question: string;
  /**
   * Bloques sueltos (p, ul, ol, Timeline…): el panel pone el espacio entre ellos y el estilo
   * base de p, listas sin clase, strong y a. Notas: `<p><small>…</small></p>`. Pasos en
   * orden: `<Timeline>` de `#root/components/ui`.
   */
  answer: React.ReactNode;
}

interface FAQPanelProps {
  faqs: FAQItem[];
  teaserText: string;
  teaserActionText?: string;
  title?: string;
  // Sección desde donde se abre (ej. "Mis Envíos"); se muestra bajo el título del drawer
  subtitle?: string;
}

/**
 * Teaser de preguntas frecuentes que abre un side-drawer con acordeón.
 * Una respuesta abierta a la vez; la primera se muestra expandida al abrir.
 */
const FAQPanel = ({
  faqs,
  teaserText,
  teaserActionText = 'Ver preguntas frecuentes',
  title = 'Preguntas frecuentes',
  subtitle,
}: FAQPanelProps) => {
  const { isDesktop } = useResponsive();
  const baseId = useId();
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleOpen = () => {
    setExpandedId(faqs[0]?.id ?? null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className={styles.faqSection}>
      <button
        type="button"
        className={styles.teaser}
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}>
        {/* Marca de agua: "?" gigante detrás del contenido, cerca de la esquina inferior izq. */}
        <IconHelp
          className={styles.teaserWatermark}
          size={96}
          stroke={1.25}
          aria-hidden="true"
        />
        <span className={styles.teaserIcon}>
          <IconHelp size={18} />
        </span>
        <span className={styles.teaserText}>{teaserText}</span>
        <span className={styles.teaserAction}>
          <span className={styles.teaserActionLabel}>{teaserActionText}</span>
          <IconChevronRight size={16} className={styles.teaserChevron} />
        </span>
      </button>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          className: isDesktop ? styles.drawerPaperDesktop : styles.drawerPaperMobile,
        }}
        style={isDesktop ? undefined : { zIndex: 1300 }}>
        <DrawerAppbar
          isMobile={!isDesktop}
          title={title}
          subtitle={subtitle}
          onClose={handleClose}
        />
        <div className={styles.faqList}>
          {faqs.map((faq, index) => {
            const isExpanded = expandedId === faq.id;
            const buttonId = `${baseId}-faq-${index}`;
            const panelId = `${baseId}-faq-${index}-panel`;

            return (
              <div
                key={faq.id}
                className={clsx(styles.faqItem, isExpanded && styles.faqItemExpanded)}>
                <h3 className={styles.questionHeading}>
                  <button
                    type="button"
                    id={buttonId}
                    className={styles.questionButton}
                    onClick={() => handleToggle(faq.id)}
                    aria-expanded={isExpanded}
                    aria-controls={panelId}>
                    <span>{faq.question}</span>
                    <IconChevronDown size={18} className={styles.chevron} />
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={styles.answerWrap}>
                  <div className={styles.answerInner}>
                    <div className={styles.answer}>{faq.answer}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Drawer>
    </div>
  );
};

export default FAQPanel;
